import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../shared/api/httpClient';
import { clientHeaders } from '../../shared/lib/device';

// Heat-discovery thresholds are OPERATIONS-CONFIGURABLE: the source of truth is
// the backend (GET /api/config/social) so ops can retune "what counts as a whale
// / a hot hand" without a client release. The frontend only holds safe defaults
// used until that endpoint exists — every field degrades to these on 404/error.
export type SocialConfig = {
  whale: {
    // A "big R" bet: stake at least this many times the room's current-round
    // median stake, OR over an absolute floor. Either qualifies.
    minMultipleOfRoomMedian: number;
    minAbsoluteBrl: number;
    minAbsoluteOther: number;
  };
  hot: {
    // A "hot hand": these are read from backend per-user heat fields; the floors
    // guard against a 1-win-1-bet fluke being surfaced as hot.
    minStreak: number;
    minWindowBets: number;
    minWindowWinRate: number;
    windowRounds: number;
    windowMinutes: number;
  };
  cold: {
    // A "cold hand" (🧊): the mirror of hot — a recent losing streak / high loss
    // rate — surfaced so others can fade (bet the opposite). Same sample floor.
    minLossStreak: number;
    minWindowLossRate: number;
    minWindowBets: number;
    windowMinutes: number;
  };
};

export const DEFAULT_SOCIAL_CONFIG: SocialConfig = {
  whale: {
    minMultipleOfRoomMedian: 10,
    minAbsoluteBrl: 500,
    minAbsoluteOther: 100,
  },
  hot: {
    minStreak: 3,
    minWindowBets: 5,
    minWindowWinRate: 0.6,
    windowRounds: 10,
    windowMinutes: 30,
  },
  cold: {
    minLossStreak: 3,
    minWindowLossRate: 0.6,
    minWindowBets: 5,
    windowMinutes: 30,
  },
};

type PartialSocialConfig = {
  whale?: Partial<SocialConfig['whale']>;
  hot?: Partial<SocialConfig['hot']>;
  cold?: Partial<SocialConfig['cold']>;
};

// Backend may send snake_case; accept both and fall back field-by-field so a
// partial payload never drops us below a working default.
function normalizeSocialConfig(raw: unknown): SocialConfig {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
  const whale = (data.whale || {}) as Record<string, any>;
  const hot = (data.hot || {}) as Record<string, any>;
  const cold = (data.cold || {}) as Record<string, any>;
  const num = (value: unknown, fallback: number) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  const d = DEFAULT_SOCIAL_CONFIG;
  return {
    whale: {
      minMultipleOfRoomMedian: num(whale.minMultipleOfRoomMedian ?? whale.min_multiple_of_room_median, d.whale.minMultipleOfRoomMedian),
      minAbsoluteBrl: num(whale.minAbsoluteBrl ?? whale.min_absolute_brl, d.whale.minAbsoluteBrl),
      minAbsoluteOther: num(whale.minAbsoluteOther ?? whale.min_absolute_other, d.whale.minAbsoluteOther),
    },
    hot: {
      minStreak: num(hot.minStreak ?? hot.min_streak, d.hot.minStreak),
      minWindowBets: num(hot.minWindowBets ?? hot.min_window_bets, d.hot.minWindowBets),
      minWindowWinRate: num(hot.minWindowWinRate ?? hot.min_window_win_rate, d.hot.minWindowWinRate),
      windowRounds: num(hot.windowRounds ?? hot.window_rounds, d.hot.windowRounds),
      windowMinutes: num(hot.windowMinutes ?? hot.window_minutes, d.hot.windowMinutes),
    },
    cold: {
      minLossStreak: num(cold.minLossStreak ?? cold.min_loss_streak, d.cold.minLossStreak),
      minWindowLossRate: num(cold.minWindowLossRate ?? cold.min_window_loss_rate, d.cold.minWindowLossRate),
      minWindowBets: num(cold.minWindowBets ?? cold.min_window_bets, d.cold.minWindowBets),
      windowMinutes: num(cold.windowMinutes ?? cold.window_minutes, d.cold.windowMinutes),
    },
  };
}

function fetchSocialConfig(): Promise<SocialConfig> {
  return apiFetch<unknown>('/api/config/social', {
    headers: { ...clientHeaders() },
  }).then(normalizeSocialConfig);
}

// One fetch per session, silent fallback to defaults. Never surfaces an error
// (retry off, no throw to the UI) — a missing config must not break a room.
export function useSocialConfig(): SocialConfig {
  const query = useQuery({
    queryKey: ['social-config'],
    queryFn: fetchSocialConfig,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  return query.data ?? DEFAULT_SOCIAL_CONFIG;
}
